import { Pause, Play, Turtle } from 'lucide-react';
import { player, type PlayResult } from '../audio/player';
import { usePlayback } from '../audio/usePlayback';

interface Props {
  src: string;
  tag?: string;
  label: string;
  /** What screen readers hear, when the visible label is short. */
  ariaLabel?: string;
  rate?: number;
  from?: number;
  to?: number;
  variant?: 'pill' | 'big' | 'icon';
  onResult?: (result: PlayResult) => void;
}

/** Progress drawn inside the disc, as a light track and a sweep, so the disc keeps its shape. */
function Ring({ progress, size }: { progress: number; size: number }) {
  const width = size > 48 ? 3 : 2;
  const r = size / 2 - width - 3;
  const c = 2 * Math.PI * r;
  return (
    <svg className="ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle className="ring-track" cx={size / 2} cy={size / 2} r={r} strokeWidth={width} />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={width} strokeDasharray={c} strokeDashoffset={c * (1 - progress)} />
    </svg>
  );
}

export function PlayButton({ src, tag, label, ariaLabel, rate = 1, from, to, variant = 'pill', onResult }: Props) {
  const id = tag ?? `${src}@${rate}`;
  const { playing, progress } = usePlayback(id);
  const slow = rate < 1;

  const onClick = () => {
    if (playing) {
      player.stop();
      return;
    }
    player.play(src, { rate, from, to, tag: id }).then((r) => onResult?.(r));
  };

  // Filled glyphs, one family in three sizes (icon, pill, big).
  const glyph = variant === 'big' ? 30 : 18;
  const icon = playing ? (
    <Pause size={glyph} aria-hidden="true" fill="currentColor" strokeWidth={0} />
  ) : slow ? (
    <Turtle size={glyph} aria-hidden="true" />
  ) : (
    <Play size={glyph} aria-hidden="true" fill="currentColor" strokeWidth={1.5} strokeLinejoin="round" style={{ marginLeft: 2 }} />
  );
  const size = variant === 'big' ? 72 : 36;

  if (variant === 'icon') {
    return (
      <button type="button" className="icon-btn" onClick={onClick} aria-label={ariaLabel ?? label} aria-pressed={playing}>
        {icon}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`play ${variant === 'big' ? 'play-big' : ''}`}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      aria-pressed={playing}
    >
      <span className="play-icon">
        {icon}
        {playing && <Ring progress={progress} size={size} />}
      </span>
      {variant !== 'big' && <span>{label}</span>}
    </button>
  );
}
